import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { audio } from "@/lib/audio";
import { Pencil, Brush, Eraser, Undo2, Redo2, Trash2 } from "lucide-react";

const W = 640, H = 480;
const COLORS = ["#1C1B1A", "#E23B44", "#2D7DD2", "#3E9E42", "#F2A900", "#8E24AA", "#E86AA6", "#7A4B26"];

export const DrawingCanvas = forwardRef(function DrawingCanvas({ onStroke, disabled }, ref) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [tool, setTool] = useState("pencil");
  const [color, setColor] = useState("#1C1B1A");
  const [size, setSize] = useState(6);

  useImperativeHandle(ref, () => ({
    getDataURL: () => canvasRef.current ? canvasRef.current.toDataURL("image/png") : null,
  }));

  useEffect(() => {
    const cv = canvasRef.current;
    cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, W, H);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctxRef.current = ctx;
    undoStack.current = [ctx.getImageData(0, 0, W, H)];
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) * (W / r.width);
    const y = (e.clientY - r.top) * (H / r.height);
    return { x, y };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    const ctx = ctxRef.current;
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, (tool === "eraser" ? size * 1.6 : size) / 2, 0, Math.PI * 2);
    ctx.fillStyle = tool === "eraser" ? "#FFFFFF" : color;
    ctx.fill();
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const p = pos(e);
    ctx.strokeStyle = tool === "eraser" ? "#FFFFFF" : color;
    ctx.lineWidth = tool === "eraser" ? size * 1.8 : (tool === "brush" ? size * 1.6 : size);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const ctx = ctxRef.current;
    undoStack.current.push(ctx.getImageData(0, 0, W, H));
    if (undoStack.current.length > 25) undoStack.current.shift();
    redoStack.current = [];
    onStroke && onStroke(canvasRef.current.toDataURL("image/png"));
  };

  const restore = (img) => {
    ctxRef.current.putImageData(img, 0, 0);
    onStroke && onStroke(canvasRef.current.toDataURL("image/png"));
  };
  const undo = () => {
    if (undoStack.current.length <= 1) return;
    audio.play("click");
    redoStack.current.push(undoStack.current.pop());
    restore(undoStack.current[undoStack.current.length - 1]);
  };
  const redo = () => {
    if (!redoStack.current.length) return;
    audio.play("click");
    const img = redoStack.current.pop();
    undoStack.current.push(img);
    restore(img);
  };
  const clear = () => {
    audio.play("click");
    const ctx = ctxRef.current;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, W, H);
    undoStack.current.push(ctx.getImageData(0, 0, W, H));
    redoStack.current = [];
    onStroke && onStroke(canvasRef.current.toDataURL("image/png"));
  };

  const ToolBtn = ({ active, onClick, children, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className="btn-sketch p-2.5"
      style={{ background: active ? "#FFDD2D" : "#FDFBF7" }}
    >{children}</button>
  );

  return (
    <div className="w-full">
      <div className="relative w-full" style={{ maxWidth: 640, margin: "0 auto" }}>
        <div className="tape" style={{ top: -12, left: "40%", transform: "rotate(-4deg)" }} />
        <canvas
          ref={canvasRef}
          data-testid="drawing-canvas"
          className="draw-canvas w-full card-flat"
          style={{ aspectRatio: `${W}/${H}`, background: "#FFFFFF", cursor: disabled ? "not-allowed" : "crosshair", touchAction: "none" }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
        />
        {disabled && (
          <div className="absolute inset-0 flex items-center justify-center bg-cream/70 font-hand text-2xl font-bold">Pencils down!</div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <ToolBtn testid="tool-pencil" active={tool === "pencil"} onClick={() => { setTool("pencil"); audio.play("click"); }}><Pencil size={18} strokeWidth={2.5} /></ToolBtn>
        <ToolBtn testid="tool-brush" active={tool === "brush"} onClick={() => { setTool("brush"); audio.play("click"); }}><Brush size={18} strokeWidth={2.5} /></ToolBtn>
        <ToolBtn testid="tool-eraser" active={tool === "eraser"} onClick={() => { setTool("eraser"); audio.play("click"); }}><Eraser size={18} strokeWidth={2.5} /></ToolBtn>
        <div className="w-px h-8 bg-ink/30 mx-1" />
        <input type="range" min="2" max="26" value={size} data-testid="brush-size" onChange={(e) => setSize(parseInt(e.target.value))} className="w-24 accent-marker-blue" />
        <div className="w-px h-8 bg-ink/30 mx-1" />
        <ToolBtn testid="canvas-undo" onClick={undo}><Undo2 size={18} strokeWidth={2.5} /></ToolBtn>
        <ToolBtn testid="canvas-redo" onClick={redo}><Redo2 size={18} strokeWidth={2.5} /></ToolBtn>
        <ToolBtn testid="canvas-clear" onClick={clear}><Trash2 size={18} strokeWidth={2.5} /></ToolBtn>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        {COLORS.map((c) => (
          <button
            key={c}
            data-testid={`color-${c}`}
            onClick={() => { setColor(c); setTool(tool === "eraser" ? "pencil" : tool); audio.play("click"); }}
            className="rounded-full"
            style={{ width: 30, height: 30, background: c, border: color === c ? "3px solid #1C1B1A" : "2px solid rgba(28,27,26,0.4)", transform: color === c ? "scale(1.15)" : "none" }}
            aria-label={`color ${c}`}
          />
        ))}
      </div>
    </div>
  );
});
